/**
 * Audit log writer (FR-AUD-01 to FR-AUD-06).
 *
 * Append-only: `firestore.rules` denies update and delete on this collection to every
 * client including the owner, so there is deliberately no edit path here to match.
 *
 * Kept indefinitely (FR-AUD-03) and surviving the purge of its subject (FR-DEL-03),
 * which is why entries store `collection` + `documentId` as plain strings rather than a
 * reference that would dangle once the record is gone.
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  type QueryConstraint,
  type WriteBatch,
} from 'firebase/firestore';
import {
  deletionChanges,
  diffFields,
  type AuditAction,
  type AuditChange,
} from '../core/audit';
import { db } from './firebase';
import { trackWrite } from './syncState';

export type AuditContext = 'bulk-reclassify' | 'import' | 'restore' | 'seed' | null;

export interface WriteAuditOptions {
  action: AuditAction;
  collection: string;
  documentId: string;
  changes: AuditChange[];
  by: string;
  context?: AuditContext;
}

export async function writeAudit(options: WriteAuditOptions): Promise<void> {
  // Nothing changed, nothing to record.
  if (options.changes.length === 0) return;

  await trackWrite(
    addDoc(collection(db, 'auditLog'), {
      at: serverTimestamp(),
      by: options.by,
      action: options.action,
      collection: options.collection,
      documentId: options.documentId,
      changes: options.changes,
      context: options.context ?? null,
    }),
  );
}

/**
 * Stage an audit entry onto an existing batch.
 *
 * Bulk reclassify writes **one entry per changed transaction** (FR-CAT-10), not one for
 * the batch. Putting them in the same batch as the transaction updates is what makes the
 * pair atomic: a change without its audit entry, or an entry for a change that did not
 * land, are both worse than the write failing outright.
 */
export function stageAudit(batch: WriteBatch, options: WriteAuditOptions): void {
  if (options.changes.length === 0) return;

  batch.set(doc(collection(db, 'auditLog')), {
    at: serverTimestamp(),
    by: options.by,
    action: options.action,
    collection: options.collection,
    documentId: options.documentId,
    changes: options.changes,
    context: options.context ?? null,
  });
}

/** Convenience: diff two versions and log only if something actually differs. */
export async function auditUpdate(
  collectionName: string,
  documentId: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  by: string,
  context: AuditContext = null,
): Promise<void> {
  await writeAudit({
    action: 'update',
    collection: collectionName,
    documentId,
    changes: diffFields(before, after),
    by,
    context,
  });
}

export async function auditDelete(
  collectionName: string,
  documentId: string,
  snapshot: Record<string, unknown>,
  by: string,
  context: AuditContext = null,
): Promise<void> {
  await writeAudit({
    action: 'delete',
    collection: collectionName,
    documentId,
    changes: deletionChanges(snapshot),
    by,
    context,
  });
}

// ---------------------------------------------------------------------------
// Reading (FR-AUD-06)
// ---------------------------------------------------------------------------

export interface AuditEntryRecord {
  id: string;
  at: Date | null;
  by: string;
  action: AuditAction;
  collection: string;
  documentId: string;
  changes: AuditChange[];
  context: AuditContext;
}

export interface AuditQuery {
  /** Bounded like every other query (SD-08) — the log is kept indefinitely. */
  collection?: string;
  limit?: number;
}

/**
 * The audit log, newest first (FR-AUD-06).
 *
 * Bounded by `limit` rather than by a period, because the useful question here is "what
 * happened recently", and the log is the one collection with no retention policy at all
 * (FR-AUD-03) — an unbounded read of it grows without limit for the life of the app.
 *
 * The `collection` filter uses the `(collection, at)` composite index that already exists
 * in `firestore.indexes.json`.
 */
export function watchAuditLog(
  spec: AuditQuery,
  onChange: (entries: AuditEntryRecord[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const constraints: QueryConstraint[] = [];
  if (spec.collection) constraints.push(where('collection', '==', spec.collection));
  constraints.push(orderBy('at', 'desc'), limit(spec.limit ?? 200));

  return onSnapshot(
    query(collection(db, 'auditLog'), ...constraints),
    (snapshot) =>
      onChange(
        snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            at: (data.at as { toDate?: () => Date })?.toDate?.() ?? null,
            by: (data.by as string) ?? '',
            action: (data.action as AuditAction) ?? 'update',
            collection: (data.collection as string) ?? '',
            documentId: (data.documentId as string) ?? '',
            changes: (data.changes as AuditChange[]) ?? [],
            context: (data.context as AuditContext) ?? null,
          };
        }),
      ),
    (error) => onError?.(error),
  );
}

/** Entries for one record, including ones that outlived it (FR-DEL-03). */
export async function auditFor(
  collectionName: string,
  documentId: string,
): Promise<AuditEntryRecord[]> {
  const snapshot = await getDocs(
    query(
      collection(db, 'auditLog'),
      where('collection', '==', collectionName),
      where('documentId', '==', documentId),
    ),
  );

  return snapshot.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        at: (data.at as { toDate?: () => Date })?.toDate?.() ?? null,
        by: (data.by as string) ?? '',
        action: (data.action as AuditAction) ?? 'update',
        collection: (data.collection as string) ?? '',
        documentId: (data.documentId as string) ?? '',
        changes: (data.changes as AuditChange[]) ?? [],
        context: (data.context as AuditContext) ?? null,
      };
    })
    .sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0));
}
